import {FC, useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import axios from 'axios';
import config from '../../../config/config';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import styles from './SelectDateTimeStep.module.scss';

type Props = {
    doctorId: number;
};

dayjs.locale('ru');

const SelectDateTimeStep: FC<Props> = ({doctorId}) => {
    const navigate = useNavigate();
    const [availableDates, setAvailableDates] = useState<any[]>([]);
    const [availableSlots, setAvailableSlots] = useState<any>({});
    const [isPreload, setPreload] = useState<boolean | null>(false);
    const [currentWeek, setCurrentWeek] = useState(0);

    // Определяем временные слоты с 10:00 до 19:00 с интервалом 30 минут
    const timeSlots = Array.from({length: 18}, (_, i) => {
        const hour = 10 + Math.floor(i / 2);
        const minute = i % 2 === 0 ? '00' : '30';
        return `${hour.toString().padStart(2, '0')}:${minute}`;
    });

    // Получаем дни текущей недели (с понедельника по воскресенье)
    const getWeekDays = (weekOffset: number = 0) => {
        const today = dayjs();
        // With `dayjs.locale('ru')`, `startOf('week')` is Monday.
        const startOfWeek = today.startOf('week'); // Понедельник
        const days = [];

        for (let i = 0; i < 7; i++) {
            const date = startOfWeek.add(weekOffset * 7 + i, 'day');
            days.push({
                date: date.format('YYYY-MM-DD'),
                day: date.format('DD'),
                month: date.format('MMMM'), // апрель, май, ...
                weekday: date.format('dd'), // пн, вт, ...
                isToday: date.isSame(today, 'day'),
            });
        }

        return days;
    };

    const fetchData = () => {
        if (config.url_provet_api) {
            setPreload(true);

            axios
                .get(`${config.url_provet_api}available_datetime?doctor_id=` + doctorId, {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                .then((response) => {
                    const available_datetime = response.data.response.rows;
                    const dates = available_datetime.map((datetime: any) => datetime.date);
                    setAvailableDates(dates);

                    // Группируем слоты по датам
                    const slotsByDate: any = {};
                    available_datetime.forEach((datetime: any) => {
                        slotsByDate[datetime.date] = datetime.slots || [];
                    });
                    setAvailableSlots(slotsByDate);
                    setPreload(false);
                })
                .catch(() => {
                    setAvailableDates([]);
                    setAvailableSlots({});
                    setPreload(null);
                });
        }
    };

    useEffect(() => {
        fetchData();
    }, [doctorId]);

    const handleSlotClick = (date: string, time: string) => {
        const startTime = `${date}T${time}:00`;
        const endTime = dayjs(startTime).add(30, 'minutes').format('YYYY-MM-DDTHH:mm:ss');

        navigate(`/online_record?doctor_id=${doctorId}&start=${startTime}&end=${endTime}`);
    };

    const handleNextWeek = () => {
        setCurrentWeek(prev => prev + 1);
    };

    const handlePrevWeek = () => {
        setCurrentWeek(prev => Math.max(0, prev - 1));
    };

    const isSlotAvailable = (date: string, time: string) => {
        if (!availableSlots[date]) return false;
        return availableSlots[date].includes(time);
    };

    const isDateAvailable = (date: string) => {
        return availableDates.includes(date);
    };

    const weekDays = getWeekDays(currentWeek);

    if (isPreload === true) {
        return (
            <div className={styles.section}>
                <div className={styles.inSection}>
                    <div className={styles.loadingContainer}>
                        <div className={styles.spinner}></div>
                        <p className={styles.loadingText}>Загружаем доступное время...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (isPreload === null) {
        return (
            <div className={styles.section}>
                <div className={styles.inSection}>
                    <div className={styles.errorContainer}>
                        <h2 className={styles.errorTitle}>Ошибка соединения</h2>
                        <p className={styles.errorText}>Не удалось загрузить доступное время. Пожалуйста, попробуйте
                            позже.</p>
                        <button
                            className={styles.retryButton}
                            onClick={fetchData}
                        >
                            Попробовать снова
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <section className={styles.section}>
            <div className={styles.inSection}>
                <div className={styles.container}>
                    <div className={styles.header}>
                        <div className={styles.headerContent}>
                            <h1 className={styles.title}>
                                Выберите дату и время
                            </h1>
                            <button
                                className={styles.refreshButton}
                                onClick={fetchData}
                                disabled={isPreload}
                            >
                                <span className={styles.refreshIcon}>↻</span>
                                Обновить
                            </button>
                        </div>

                        <p className={styles.subtitle}>
                            Выберите удобное время для записи на прием. Доступные слоты подсвечены зеленым цветом.
                        </p>
                    </div>

                    <div className={styles.weekNavigation}>
                        <button
                            className={styles.navButton}
                            onClick={handlePrevWeek}
                            disabled={currentWeek === 0}
                        >
                            ← Предыдущая неделя
                        </button>
                        
                        <div className={styles.weekInfo}>
                            {weekDays[0].month.toUpperCase()} {weekDays[0].day} - {weekDays[6].month.toUpperCase()} {weekDays[6].day}
                            {currentWeek === 0 && <span className={styles.currentWeekLabel}> (текущая неделя)</span>}
                        </div>

                        <button
                            className={styles.navButton}
                            onClick={handleNextWeek}
                        >
                            Следующая неделя →
                        </button>
                    </div>

                    <div className={styles.scheduleTable}>
                        {weekDays.map(day => {
                            return (
                                <div
                                    key={day.date}
                                    className={`${styles.dayColumn} ${!isDateAvailable(day.date) ? styles.dayUnavailable : ''}`}
                                >
                                    <div className={`${styles.dayHeader} ${day.isToday ? styles.today : ''}`}>
                                        <div className={styles.weekday}>{day.weekday}</div>
                                        <div className={styles.date}>
                                            <span className={styles.dayNum}>{day.day}</span>{' '}
                                            <span className={styles.monthText}>{day.month}</span>
                                            {day.isToday && <span className={styles.todayBadge}>Сегодня</span>}
                                        </div>
                                        {!isDateAvailable(day.date) && (
                                            <div className={styles.unavailableBadge}>Недоступно</div>
                                        )}
                                    </div>

                                    <div className={styles.daySlots}>
                                        {timeSlots.map(time => {
                                            const isAvailable = isSlotAvailable(day.date, time);
                                            return (
                                                <button
                                                    key={`${day.date}-${time}`}
                                                    className={`${styles.slot} ${isAvailable ? styles.slotAvailable : styles.slotUnavailable}`}
                                                    onClick={() => isAvailable && handleSlotClick(day.date, time)}
                                                    disabled={!isAvailable}
                                                    title={isAvailable ? `Выбрать ${day.date} ${time}` : 'Время недоступно'}
                                                >
                                                    {isAvailable ? (
                                                        <>
                                                            <span className={styles.slotTime}>{time}</span>
                                                            <span className={styles.slotStatus}>✓</span>
                                                        </>
                                                    ) : (
                                                        <span className={styles.slotTimeDisabled}>{time}</span>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className={styles.legend}>
                        <div className={styles.legendItem}>
                            <div className={`${styles.legendColor} ${styles.available}`}></div>
                            <span>Доступное время</span>
                        </div>
                        <div className={styles.legendItem}>
                            <div className={`${styles.legendColor} ${styles.unavailable}`}></div>
                            <span>Недоступное время</span>
                        </div>
                        <div className={styles.legendItem}>
                            <div className={`${styles.legendColor} ${styles.selected}`}></div>
                            <span>Сегодняшний день</span>
                        </div>
                    </div>

                    <div className={styles.note}>
                        <p className={styles.noteText}>
                            💡 Каждый прием длится 30 минут. Пожалуйста, выберите удобное для вас время.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default SelectDateTimeStep;
